// The photos attached to a logbook's entries, for the strip under each row.
//
// ONE S3 LIST FOR THE WHOLE PAGE. Attachments are keyed
// `${itemId}/logbook/${entryId}/<file>` (Urls.urlForLogbookEntryStorage), so a
// single listing of `${itemId}/logbook/` carries every entry's photos and the
// grouping is a path segment. Not N per-entry lists: a trip is commonly 5-30
// entries and each list is its own round trip.
//
// The deeper reason is that an item-wide map is immune to the entry list
// changing. LogbookTrip's Rename and Combine both replace `entries` wholesale,
// and Combine ADDS entries to the page -- a fetch keyed on the entry ids would
// have to re-run mid-write, against rows AppSync has just echoed back. This map
// is a superset of every entry that can ever appear on the page, and the ids it
// is keyed by are stable through both, so nothing refetches. That is also why
// there is no `entryIds` parameter: it isn't a fetch input, it would only
// filter a map the table already looks up per row, and an array prop is a new
// identity on every render -- exactly the dep that would reintroduce the
// refetch this shape exists to avoid.
//
// The listing spans every LOGBOOK on the item (there is no logbookId in the
// path). Those land under ids this page never looks up, which costs a little
// metadata and nothing else -- don't "fix" it with a logbookId filter, because
// the prefix cannot express one.
//
// `photosByEntry` is `null` until an answer exists and a Map once one does.
// That distinction is load-bearing, the same way `lines` is for a tide popup:
// null means "not asked", an empty Map means "asked, nothing there". Collapse
// them and an entry whose photos haven't loaded reads as an entry with none.
//
// THE FLEET VARIANT. The Trips page lists every item's entries in one table,
// and the prefix is per item, so `useFleetEntryPhotos(itemIds)` lists one
// prefix per item and merges the Maps (entry ids are UUIDs, unique across
// items). The effect is keyed on the ids JOINED AS A STRING, sorted and
// deduped -- that is what makes the array argument safe where an `entryIds`
// array would not have been: a fresh array identity carrying the same ids is
// the same key and refetches nothing. `useEntryPhotos(itemId)` is that hook
// over a one-element list, and its suite standing unedited is the proof that
// the single-item behaviour did not move.
//
// One item's listing failing must not blank the fleet: the merged Map holds
// whatever answered, the error is reported the same way, and the read is left
// unmarked so switching photos off and on retries the lot -- the existing
// "the toggle is the retry" rule. With one item that collapses to the old
// behaviour exactly: nothing answered, so `photosByEntry` stays null.
//
// THE TASK VARIANT. `${itemId}/task/` has the same `<root>/<id>/<file>` shape
// as `${itemId}/logbook/` -- one folder per task instead of one per entry -- so
// the task lists want this hook exactly, with a different prefix builder. That
// is the `storageRoot` option, and `src/pages/task/useTaskPhotos.js` is four
// lines over it. Nothing else here is logbook-specific.
//
// THE EFFECT IS KEYED ON THE BUILT ROOTS, not on the ids. That is what makes
// one hook safe for two prefixes: `<id>/logbook/` and `<id>/task/` are
// different keys for the same item, so a page mounting both gets two listings
// and neither can serve the other's rows from a stale map. Keying on the ids
// and treating the builder as a free variable would have made them one key.
// The roots are built from the sorted, deduped ids, so the old guarantee holds:
// a fresh array identity carrying the same ids refetches nothing.

import { useCallback, useEffect, useRef, useState } from 'react';
import { storageGet, storageList } from '../../utilities/Database.mjs';
import { urlForLogbookStorage } from '../../fleet-shared/Urls.mjs';
import { groupPhotosByEntry } from '../../fleet-shared/Attachments.mjs';

// Re-exported, not defined here: the grouping rule moved to fleet-shared so
// TaskPogoShareLogbookTrip can apply the SAME one to its S3 listing when it
// presigns a shared trip's photos. See that module for why.
export { groupPhotosByEntry };

// The effect's key for a list of item ids: order and duplicates are not
// fetch inputs, so neither may cause a refetch.
function itemsKey(itemIds) {
  return [...new Set((itemIds ?? []).filter(Boolean))].sort().join(',');
}

export function useFleetEntryPhotos(itemIds, { enabled, storageRoot = urlForLogbookStorage } = {}) {
  // Built here rather than inside the effect: this IS the effect's identity
  // (see the header), so it has to be a value the deps can compare.
  const key = itemsKey(itemIds).split(',').filter(Boolean).map(storageRoot).join(',');

  const [photosByEntry, setPhotosByEntry] = useState(null);
  // `{ kind: 'list' | 'open', error, name }`, or null. Both failures come back
  // through one channel -- the hook owns everything S3 about these photos, so
  // the page has one thing to render -- but they are TAGGED, because they are
  // not the same sentence: a failed listing is about the page, and a failed
  // open is about the photo the reader just tapped. A single untagged error
  // told someone who tapped one photo that none of them had loaded, which was
  // plainly false with five thumbnails on screen beside it.
  const [photosError, setPhotosError] = useState(null);

  // The key the map in state belongs to. Turning the switch OFF keeps the map
  // and turning it back on does not refetch -- the one read is the whole cost
  // of the feature for the life of the page. Left unset on failure, so
  // toggling off and on is the retry, deliberately the only one: a Retry
  // button for a strip of thumbnails is more chrome than the problem.
  const loadedFor = useRef(null);

  // The key the effect last ran for, which is NOT the same question as
  // `loadedFor` -- that one is only set by a SUCCESSFUL read. Clearing stale
  // state off `loadedFor` would skip exactly the case that needs it: an item
  // whose listing failed never set it, so its error would outlive the
  // navigation and land on the next item's page.
  const lastKey = useRef(null);

  // window.location.href after an in-SPA navigation would yank the reader off
  // whatever page they are on now.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    // The reset runs BEFORE the empty-key exit, so a list that empties out
    // clears what the previous list loaded rather than leaving it to show
    // under whatever rows come next.
    if (lastKey.current !== key) {
      lastKey.current = key;
      loadedFor.current = null;
      setPhotosByEntry(null);
      setPhotosError(null);
    }
    if (!key || !enabled || loadedFor.current === key) return undefined;

    let ignore = false;
    const roots = key.split(',');

    Promise.allSettled(
      roots.map((root) => storageList(root).then((items) => groupPhotosByEntry(items, root)))
    ).then((results) => {
      if (ignore) return;
      const failed = results.find((r) => r.status === 'rejected');
      const merged = new Map();
      for (const r of results) {
        if (r.status !== 'fulfilled') continue;
        for (const [entryId, photos] of r.value) merged.set(entryId, photos);
      }
      if (failed) {
        // Never the page's own StatusMessage: that belongs to the entries read,
        // and routing an S3 failure there would make an unreadable prefix look
        // like a page that failed to load. The page shows its own dismissible
        // warning and the table renders exactly as it does with photos off.
        console.warn('Could not list the photos for this prefix:', failed.reason);
        setPhotosError({ kind: 'list', error: failed.reason });
        // What DID answer is still shown; null only when nothing did, so a
        // single item keeps reading as "not asked" rather than "nothing there".
        if (merged.size > 0 || results.length > 1) setPhotosByEntry(merged);
        return;
      }
      loadedFor.current = key;
      setPhotosError(null);
      setPhotosByEntry(merged);
    });

    return () => {
      ignore = true;
    };
  }, [key, enabled]);

  // The signed-URL fetch lives here rather than in the table, so the hook owns
  // everything S3 about an entry's photos and the page has one error channel to
  // render. Tagged `open`, because opening is a deliberate gesture that visibly
  // does nothing when it fails -- it has to say so in its own words.
  const openPhoto = useCallback(async (photo) => {
    try {
      setPhotosError(null);
      // The ORIGINAL, never the preview: the preview is what fills the strip,
      // and tapping one is the gesture for seeing the real photo.
      const file = await storageGet(photo.key);
      if (!alive.current) return;
      window.location.href = file.url.href;
    } catch (err) {
      console.warn(`Could not open ${photo.name}:`, err);
      if (alive.current) setPhotosError({ kind: 'open', error: err, name: photo.name });
    }
  }, []);

  return { photosByEntry, photosError, openPhoto };
}

export function useEntryPhotos(itemId, options) {
  return useFleetEntryPhotos(itemId ? [itemId] : [], options);
}

export default useEntryPhotos;
